import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Autocomplete,
  Divider,
  Card,
  InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KitchenIcon from '@mui/icons-material/Kitchen';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import { useNavigate } from 'react-router-dom';
import { useGetLocationsQuery, useCreateConsumptionMutation } from '../../../store/api/inventoryApi';
import { useGetProductsQuery } from '../../../store/api/productsApi';
import ProductAutocomplete from '../../../shared/components/ProductAutocomplete';

function ConsumptionForm() {
  const navigate = useNavigate();
  const { data: locationsData } = useGetLocationsQuery();
  const [createConsumption, { isLoading: isSaving }] = useCreateConsumptionMutation();

  const [formData, setFormData] = useState({
    locationId: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    items: []
  });

  const [error, setError] = useState(null);

  const handleAddItem = (product) => {
    if (!product) return;

    // Check if already added
    if (formData.items.some(item => item.productId === product.id)) {
      setError('Product already added to list');
      return;
    }

    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          productId: product.id,
          name: product.name,
          sku: product.sku,
          unit: product.unit || 'Kg',
          quantity: 1,
        }
      ]
    });
    setError(null);
  };

  const handleRemoveItem = (index) => {
    const newItems = [...formData.items];
    newItems.splice(index, 1);
    setFormData({ ...formData, items: newItems });
  };

  const handleQuantityChange = (index, value) => {
    const newItems = [...formData.items];
    newItems[index].quantity = Number(value);
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async () => {
    if (!formData.locationId) {
      setError('Please select a Kitchen Location');
      return;
    }
    if (formData.items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    try {
      await createConsumption(formData).unwrap();
      navigate('/inventory/consumptions');
    } catch (err) {
      setError(err.data?.message || 'Failed to save consumption record');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/inventory/consumptions')} sx={{ color: '#F57C00' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
          New Stock Clearance (Kitchen)
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Header Section */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Autocomplete
                    options={locationsData || []}
                    getOptionLabel={(option) => option.name}
                    value={(locationsData || []).find(l => l.id === formData.locationId) || null}
                    onChange={(e, value) => setFormData({ ...formData, locationId: value?.id || '' })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Source Kitchen Location"
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <KitchenIcon sx={{ color: '#F57C00' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Consumption Date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Notes / Reason"
                    placeholder="e.g. Daily shift consumption"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Grid>

        {/* Items Section */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingBasketIcon sx={{ color: '#F57C00' }} /> Add Ingredients
                </Typography>
                <ProductAutocomplete
                  onSelect={handleAddItem}
                  label="Search product to clear from stock..."
                />
              </Box>

              <TableContainer sx={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 2 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#F8F9FA' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Quantity used</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Unit</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No ingredients added yet. Search and select ingredients above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      formData.items.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{item.name}</TableCell>
                          <TableCell>{item.sku}</TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleQuantityChange(index, e.target.value)}
                              sx={{ width: 100 }}
                              inputProps={{ min: 0.001, step: 0.1 }}
                            />
                          </TableCell>
                          <TableCell align="center">{item.unit}</TableCell>
                          <TableCell align="center">
                            <IconButton onClick={() => handleRemoveItem(index)} color="error" size="small">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/inventory/consumptions')}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  disabled={isSaving}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
                    }
                  }}
                >
                  {isSaving ? 'Processing...' : 'Submit Clearance'}
                </Button>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ConsumptionForm;
